import type { ReactNode } from 'react'
import { Link, useNavigate } from 'react-router-dom'
import { logoutTemporaryAdmin } from '../../auth/adminAuth'
import { useConfirmDialog } from '../../context/ConfirmDialogContext'

import type { NavItem } from '../../types'

type NavActionProps = {
  item: NavItem
  active?: boolean
  onSelect?: (id: string) => void
  className?: string
  /** Set on the icon-only rail, where the label is not rendered. */
  label?: string
  children: ReactNode
}

/**
 * Renders a nav entry as a link once it has a route and as an inert button until then,
 * so the rail and the expanded panel stay in sync as pages get built out.
 */
export const NavAction = ({ item, active = false, onSelect, className, label, children }: NavActionProps) => {
  const navigate = useNavigate()
  const { confirm, showSuccess } = useConfirmDialog()

  if (item.id === 'logout') {
    return (
      <button
        type="button"
        className={className}
        aria-label={label || 'Log out'}
        title={label || 'Log out'}
        onClick={async (e) => {
          e.preventDefault()
          e.stopPropagation()
          const confirmed = await confirm({
            title: 'Sign Out',
            message: 'Are you sure you want to sign out of the Salzburg Cab dashboard?',
            confirmText: 'Sign Out',
            cancelText: 'Cancel',
            variant: 'warning',
            icon: 'logout',
          })
          if (!confirmed) return
          await showSuccess({
            title: 'Signed Out Successfully',
            message: 'You have been securely signed out. Redirecting to login...',
            autoCloseMs: 1200,
          })
          logoutTemporaryAdmin()
          navigate('/admin-login', { replace: true })
        }}
      >
        {children}
      </button>
    )
  }

  const shared = {
    className,
    'aria-current': active ? ('page' as const) : undefined,
    'aria-label': label,
    title: label,
    onClick: () => onSelect?.(item.id),
  }

  if (item.path) {
    return (
      <Link to={item.path} {...shared}>
        {children}
      </Link>
    )
  }

  return (
    <button type="button" {...shared}>
      {children}
    </button>
  )
}
